/**
 * OptionCampaignMonitor — tracks staged scale-in campaigns (the options analog of the
 * futures E1/E2/E3 scale-in). Shows each campaign's stages (deployed/armed), deployed vs
 * reserve capital, aggregate live P&L, and the hard stop; lets the user launch a new one
 * or close an active one.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, Button, Chip, Stack, Divider, CircularProgress, Dialog, DialogTitle, DialogContent, IconButton, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import toast from 'react-hot-toast';
import { optionsService } from '../../services/options.service';
import StagedCampaignDialog from './StagedCampaignDialog';
import CampaignAdjustPanel from './CampaignAdjustPanel';
import { thetaVerdict, deltaVerdict, vegaVerdict } from './campaignGreeks';

const fmtInr = (v: number) => `₹${Math.round(v).toLocaleString('en-IN')}`;
const fmtSigned = (v: number) => `${v >= 0 ? '+' : '−'}${fmtInr(Math.abs(v))}`;
const fmtPx = (v: number) => (v != null ? Math.round(Number(v)).toLocaleString('en-IN') : '—');
const VIEW_LABEL: Record<string, string> = {
  WONT_FALL_BELOW: "Won't fall", WONT_RISE_ABOVE: "Won't rise", WILL_BREAK_ABOVE: 'Break up',
  WILL_BREAK_BELOW: 'Break down', STAY_IN_RANGE: 'In range', WILL_BREACH: 'Break either',
};
const STATUS_COLOR: Record<string, 'info' | 'success' | 'default' | 'error'> = {
  ACTIVE: 'info', COMPLETED: 'success', STOPPED: 'error', CANCELLED: 'default',
};

const legLine = (legs: any[]) => (legs || []).map((l: any) =>
  `${l.action === 'BUY' ? '+' : '−'}${l.optionType}${fmtPx(l.strike)}×${l.quantity}`).join(' ');

const CampaignCard: React.FC<{ c: any; onChanged: () => void }> = ({ c, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const agg = c.aggregate || {};
  const detailByStage: Record<number, any> = {};
  for (const d of (agg.stageDetails || [])) detailByStage[d.index] = d;
  const livePnl = Number(agg.openPnl || 0) + Number(agg.realizedPnl || 0);
  const deployed = (c.stages || []).filter((s: any) => s.status === 'DEPLOYED');
  const reserve = Math.max(0, Number(c.budgetInr || 0) - Number(agg.deployedInr || 0));
  const active = c.status === 'ACTIVE';

  const close = async () => {
    if (!window.confirm('Square off ALL tranches of this campaign now?')) return;
    setBusy(true);
    try { await optionsService.closeCampaign(c.id); toast.success('Campaign squared off'); onChanged(); }
    catch (e: any) { toast.error(e?.response?.data?.error?.message || 'Close failed'); }
    finally { setBusy(false); }
  };
  const goLive = async () => {
    if (!window.confirm('Flip this WHOLE campaign to LIVE? Every future tranche will place REAL orders on the exchange.')) return;
    setBusy(true);
    try { await optionsService.goLiveCampaign(c.id); toast.success('Campaign is now LIVE — future tranches place real orders.', { duration: 7000 }); onChanged(); }
    catch (e: any) { toast.error(e?.response?.data?.error?.message || 'Go-live failed'); }
    finally { setBusy(false); }
  };

  const barColor = active ? '#1e88e5' : c.status === 'COMPLETED' ? '#2e7d32' : c.status === 'STOPPED' ? '#c62828' : '#90a4ae';
  const pnlNow = active ? livePnl : Number(c.realizedPnl || 0);

  return (
    <Card variant="outlined" sx={{ borderLeft: `4px solid ${barColor}` }}>
      <CardContent sx={{ pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 0.75, flexWrap: 'wrap' }}>
          <Chip size="small" label={`#${c.id}`} sx={{ height: 20, fontWeight: 800 }} />
          <Typography sx={{ fontWeight: 800 }}>{c.symbol} · {VIEW_LABEL[c.view] || c.view}</Typography>
          <Chip size="small" label={c.tradingMode} color={c.tradingMode === 'PAPER' ? 'warning' : 'error'} sx={{ height: 18, fontSize: '0.58rem', fontWeight: 800 }} />
          <Chip size="small" label={c.status} color={STATUS_COLOR[c.status] || 'default'} sx={{ height: 18, fontSize: '0.58rem', fontWeight: 700, ml: 'auto' }} />
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1 }}>
          <Metric label={active ? 'P&L (live)' : 'P&L (realised)'} value={fmtSigned(pnlNow)} color={pnlNow >= 0 ? '#2e7d32' : '#c62828'} />
          <Metric label="ROI" value={`${Number(agg.roiPct || 0)}%`} color={Number(agg.roiPct || 0) >= 0 ? '#2e7d32' : '#c62828'} />
          <Metric label="Deployed" value={fmtInr(Number(agg.deployedInr || 0))} sub={`of ${fmtInr(Number(c.budgetInr || 0))}`} />
          <Metric label="Reserve" value={fmtInr(reserve)} />
          <Metric label="Tranches" value={`${deployed.length}/${c.stageCount}`} sub="live / total" />
        </Box>

        {/* Colour-coded Greeks strip (active) — green = working FOR the trade, red = against. */}
        {active && (() => {
          const tv = thetaVerdict(Number(agg.thetaInrPerDay || 0)), dv = deltaVerdict(c.view, Number(agg.netDelta || 0)), vv = vegaVerdict(c.view, Number(agg.netVega || 0));
          return (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.9 }}>
              <GreekChip label="Θ / day" value={fmtSigned(Number(agg.thetaInrPerDay || 0))} color={tv.color} tip={`Theta — ₹ the position earns (+) or bleeds (−) per day. ${tv.note}`} />
              <GreekChip label="Net Δ" value={Number(agg.netDelta || 0).toFixed(2)} color={dv.color} tip={`Net delta — directional exposure. ${dv.note}`} />
              <GreekChip label="Vega" value={fmtSigned(Number(agg.netVega || 0))} color={vv.color} tip={`Vega — ₹ P&L for a 1-pt rise in implied volatility. ${vv.note}`} />
            </Box>
          );
        })()}

        <Divider sx={{ my: 1 }} />
        <Stack spacing={0.5}>
          {(c.stages || []).map((s: any) => {
            const d = detailByStage[s.index];
            return (
              <Box key={s.index} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', fontSize: '0.72rem' }}>
                <Chip size="small" label={`E${s.index}`} sx={{ height: 17, fontSize: '0.55rem', fontWeight: 700 }} />
                <Chip size="small" label={s.status === 'DEPLOYED' ? 'deployed' : s.status === 'SKIPPED' ? 'skipped' : `armed @ ${fmtPx(s.triggerPrice)}`}
                  color={s.status === 'DEPLOYED' ? 'success' : 'default'} variant={s.status === 'DEPLOYED' ? 'filled' : 'outlined'} sx={{ height: 17, fontSize: '0.52rem' }} />
                <Typography variant="caption" color="text.secondary">{Math.round(s.sizeFraction * 100)}%</Typography>
                {d ? <>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{d.strategy}: {legLine(d.legs)}{d.expiry ? ` · exp ${d.expiry.slice(5)}` : ''}</Typography>
                  {!d.closed && <Typography variant="caption" sx={{ fontWeight: 700, color: d.livePnl >= 0 ? '#2e7d32' : '#c62828' }}>{fmtSigned(d.livePnl)}</Typography>}
                </> : s.status !== 'DEPLOYED' && <Typography variant="caption" color="text.disabled">waiting for price</Typography>}
              </Box>
            );
          })}
        </Stack>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1.2, flexWrap: 'wrap' }}>
          {c.slAmountInr != null && <Tooltip arrow title={`Binding stop — all tranches square off when total loss reaches this${c.slPct ? ` (${Math.round(Number(c.slPct) * 100)}% of the aggregate max loss)` : ''}. The danger level alerts you to adjust first, and the stop must persist a tick before it fires. Patient while the scale-in is still arming.`}><Typography variant="caption" sx={{ color: '#c62828', fontWeight: 700 }}>🛑 SL {fmtInr(Number(c.slAmountInr))}</Typography></Tooltip>}
          {(agg.tpAmountEffInr || c.tpAmountInr) != null && (agg.tpAmountEffInr || c.tpAmountInr) > 0 && <Tooltip arrow title={`Binding take-profit — books off when total profit reaches ${c.tpPct ? `${Math.round(Number(c.tpPct) * 100)}%` : '50%'} of the achievable max profit of the tranches DEPLOYED so far${agg.curMaxProfitInr ? ` (${fmtInr(Number(agg.curMaxProfitInr))})` : ''}. Always reachable; grows as more tranches deploy.`}><Typography variant="caption" sx={{ color: '#2e7d32', fontWeight: 700 }}>🎯 TP {fmtInr(Number(agg.tpAmountEffInr || c.tpAmountInr))}</Typography></Tooltip>}
          {c.hardSlPrice != null && <Typography variant="caption" color="text.secondary">⚠ danger {fmtPx(c.hardSlPrice)}</Typography>}
          {c.adjustmentNeeded && active && <Chip size="small" color="warning" label="⚠ Adjust" sx={{ height: 18, fontSize: '0.56rem', fontWeight: 800 }} />}
          {c.exitReason && <Typography variant="caption" color="text.disabled">closed · {c.exitReason}</Typography>}
          {active && <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
            <Button size="small" variant={c.adjustmentNeeded ? 'contained' : 'outlined'} color="warning" sx={{ py: 0 }} onClick={() => setAdjustOpen(true)}>Adjust</Button>
            {c.tradingMode === 'PAPER' && <Button size="small" variant="outlined" color="error" sx={{ py: 0 }} disabled={busy} onClick={goLive}>Go LIVE</Button>}
            <Button size="small" variant="outlined" sx={{ py: 0 }} disabled={busy} onClick={close}>Close all</Button>
          </Box>}
        </Box>
      </CardContent>

      <Dialog open={adjustOpen} onClose={() => setAdjustOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, pb: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          Adjust campaign #{c.id} · {c.symbol} · {VIEW_LABEL[c.view] || c.view}
          <IconButton size="small" onClick={() => setAdjustOpen(false)} sx={{ ml: 'auto' }}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {adjustOpen && <CampaignAdjustPanel campaignId={c.id} symbol={c.symbol ? `${c.symbol}USD` : undefined} tradingMode={c.tradingMode} onAdjusted={onChanged} />}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

/** Small colour-coded Greek pill with a plain-English "for/against" tooltip. */
const GreekChip: React.FC<{ label: string; value: React.ReactNode; color?: string; tip: string }> = ({ label, value, color, tip }) => (
  <Tooltip arrow title={tip}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, px: 1, py: 0.35, borderRadius: 1.5, border: t => `1px solid ${color || t.palette.divider}`, bgcolor: color ? `${color}14` : 'transparent' }}>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</Typography>
      <Typography sx={{ fontWeight: 800, fontSize: '0.78rem', color }}>{value}</Typography>
    </Box>
  </Tooltip>
);

const Metric: React.FC<{ label: React.ReactNode; value: React.ReactNode; sub?: string; color?: string }> = ({ label, value, sub, color }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</Typography>
    <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color }}>{value}</Typography>
    {sub && <Typography variant="caption" color="text.disabled">{sub}</Typography>}
  </Box>
);

const OptionCampaignMonitor: React.FC<{ seedSymbol?: string }> = ({ seedSymbol }) => {
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [dlg, setDlg] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r: any = await optionsService.listCampaigns(); setRows(r?.data?.data || []); }
    catch { /* */ } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); const id = setInterval(load, 30000); return () => clearInterval(id); }, [load]);

  const active = rows.filter(r => r.status === 'ACTIVE');
  const done = rows.filter(r => r.status !== 'ACTIVE');

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
        <Typography sx={{ fontWeight: 800 }}>Staged scale-in campaigns</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1, minWidth: 180 }}>
          Deploy 1/Nth now, add re-optimised tranches at your levels, one hard stop closes all.
        </Typography>
        <Button size="small" startIcon={<RefreshIcon fontSize="small" />} onClick={load} disabled={loading}>Refresh</Button>
        <Button size="small" variant="contained" startIcon={<AddIcon fontSize="small" />} onClick={() => setDlg(true)}>New campaign</Button>
      </Box>

      {loading && !rows.length && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}><CircularProgress size={16} /> <Typography variant="caption">Loading…</Typography></Box>}
      {!loading && !rows.length && <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No campaigns yet. Click “New campaign” to stage a scale-in from a view.</Typography>}

      <Stack spacing={1.2}>
        {active.map(c => <CampaignCard key={c.id} c={c} onChanged={load} />)}
        {done.length > 0 && active.length > 0 && <Divider sx={{ my: 0.5 }}><Typography variant="caption" color="text.secondary">Closed</Typography></Divider>}
        {done.map(c => <CampaignCard key={c.id} c={c} onChanged={load} />)}
      </Stack>

      <StagedCampaignDialog open={dlg} onClose={() => setDlg(false)} seed={seedSymbol ? { underlyingSymbol: seedSymbol } : undefined} onCreated={load} />
    </Box>
  );
};

export default OptionCampaignMonitor;
